import PropTypes from 'prop-types';
import React from 'react';

import styles from './Headline.module.scss';
import { VisitCodeBadge, VisitCodeCorner } from './VisitCode';

const visitShape = PropTypes.shape({
  code: PropTypes.string,
  kindName: PropTypes.string,
  label: PropTypes.string,
  range: PropTypes.string,
  localRange: PropTypes.string,
  hint: PropTypes.string,
});

export const CardHeadline = ({ visit, when, soon = false, aside, layout = 'stack', toggle }) => (
  <div className={styles.row}>
    <VisitCodeCorner code={visit.code} visitType={visit.kindName} />
    <div className={`${styles.cardBody} ${visit.code ? styles.afterCorner : ''}`}>
      {layout === 'inline' ? (
        <div className={styles.inlineStack}>
          <span className={`${styles.title} ${styles.inlineTitle}`}>
            {!visit.code && <VisitCodeBadge code={null} visitType={visit.kindName} />}
            {toggle ? (
              <button
                type="button"
                className={styles.toggleTitle}
                aria-expanded={toggle.open}
                onClick={toggle.onToggle}
              >
                {visit.label}
              </button>
            ) : (
              <span>{visit.label}</span>
            )}
          </span>
          {visit.range && (
            <span className={`${styles.timeLine} ${styles.inlineTime}`}>· {visit.range}</span>
          )}
        </div>
      ) : (
        <div className={styles.titleStack}>
          <span className={styles.title}>
            {!visit.code && <VisitCodeBadge code={null} visitType={visit.kindName} />}
            <span>{visit.label}</span>
          </span>
          <span className={styles.timeLine}>
            <span className={styles.timeParts}>
              {visit.range && <span className={styles.timePart}>{visit.range}</span>}
              {when && (
                <span className={`${styles.timePart} ${soon ? styles.soon : styles.when}`}>
                  {when}
                </span>
              )}
            </span>
          </span>
          {visit.localRange && visit.localRange !== visit.range && (
            <span className={`${styles.timeLine} ${styles.local}`}>Local {visit.localRange}</span>
          )}
        </div>
      )}
      {aside && <span className={styles.aside}>{aside}</span>}
    </div>
  </div>
);

export const PanelHeadline = ({
  visit,
  title,
  subtitle,
  qualifier,
  description,
  actions,
  muted = false,
  size = 'card',
  divider = false,
  className,
}) => (
  <div className={[styles.row, divider ? styles.divider : '', className].filter(Boolean).join(' ')}>
    {visit && <VisitCodeCorner code={visit.code} visitType={visit.kindName} size={size} />}
    <div className={`${styles.panelBody} ${size === 'panel' ? styles.panelBodyLarge : ''}`}>
      <div className={`${styles.panelText} ${size === 'panel' ? styles.panelTextLarge : ''}`}>
        <h2
          className={[
            styles.panelTitle,
            size === 'panel' ? styles.panelTitleLarge : '',
            muted ? styles.muted : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {title}
        </h2>
        {subtitle ||
          (visit?.label && (
            <p className={styles.visitLine}>
              {!visit.code && <VisitCodeBadge code={null} visitType={visit.kindName} />}
              <span className={styles.visitName} title={visit.hint || undefined}>
                {visit.label}
              </span>
              {qualifier && (
                <>
                  <span aria-hidden="true">·</span>
                  <span>{qualifier}</span>
                </>
              )}
            </p>
          ))}
        {description && <p className={styles.description}>{description}</p>}
      </div>
      {actions && <div className={styles.actions}>{actions}</div>}
    </div>
  </div>
);

CardHeadline.propTypes = {
  visit: visitShape.isRequired,
  when: PropTypes.string,
  soon: PropTypes.bool,
  aside: PropTypes.node,
  layout: PropTypes.oneOf(['stack', 'inline']),
  toggle: PropTypes.shape({ open: PropTypes.bool, onToggle: PropTypes.func }),
};

PanelHeadline.propTypes = {
  visit: visitShape,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  qualifier: PropTypes.node,
  description: PropTypes.node,
  actions: PropTypes.node,
  muted: PropTypes.bool,
  size: PropTypes.oneOf(['card', 'panel']),
  divider: PropTypes.bool,
  className: PropTypes.string,
};
